import React, { useState, useEffect, useCallback, useRef } from 'react';
import { devFilesApi } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  Check, Clock, Circle, Save, X, Plus, Trash2, Pencil, RefreshCw,
  ChevronLeft, ChevronRight, Flag, GripVertical, Search, ClipboardList
} from 'lucide-react';

/**
 * DevPlanPage - Development page to view/edit dev-plan.json
 * Uses unified list/detail pattern matching data modules
 * TODO: Delete this file before production deployment
 */
const DevPlanPage = () => {
  const [devPlan, setDevPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStep, setSelectedStep] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddStep, setShowAddStep] = useState(false);
  const [newStep, setNewStep] = useState({ name: '', description: '', comment: '', status: 'Not Started', type: 'Core', owner: null });
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCloseStep, setShowCloseStep] = useState(false);
  const [closeStepForm, setCloseStepForm] = useState({ summary: '', changes: '' });
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('Active');
  const [ownerFilter, setOwnerFilter] = useState('all');
  const [showReorderModal, setShowReorderModal] = useState(false);
  const [reorderItems, setReorderItems] = useState([]);
  const [showCompletePreview, setShowCompletePreview] = useState(false);
  const [completePreviewItems, setCompletePreviewItems] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(() => {
    return localStorage.getItem('pmd-current-user') || null;
  });

  const { notify } = useNotifications();

  const STEP_TYPES = ['Core', 'Feature', 'Milestone', 'UI'];
  const OWNERS = ['Josh', 'Danny', 'Todd'];

  const loadDevPlan = useCallback(async () => {
    try {
      setLoading(true);
      // error cleared
      const response = await devFilesApi.getDevPlan();
      setDevPlan(response.data);
      // Auto-select first incomplete step, or first step if all complete
      if (!selectedStep) {
        const incomplete = response.data.steps.find(s => s.status !== 'Complete');
        setSelectedStep(incomplete || response.data.steps[0] || null);
      }
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to load dev plan');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStep]);

  useEffect(() => {
    loadDevPlan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-select step when devPlan updates (after save, delete, etc.)
  useEffect(() => {
    if (devPlan && selectedStep) {
      const updated = devPlan.steps.find(s => s.step === selectedStep.step);
      if (updated) {
        setSelectedStep(updated);
      } else if (devPlan.steps.length > 0) {
        setSelectedStep(devPlan.steps[0]);
      } else {
        setSelectedStep(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [devPlan]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectStep = (step) => {
    if (isEditing) {
      notify.confirm('Discard unsaved changes?', () => {
        setIsEditing(false);
        setEditForm({});
        setSelectedStep(step);
      });
      return;
    }
    setSelectedStep(step);
  };

  const startEditing = () => {
    if (!selectedStep) return;
    setEditForm({ ...selectedStep });
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditForm({});
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await devFilesApi.updateDevPlanStep(editForm.step, editForm);
      setIsEditing(false);
      setEditForm({});
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to save step');
    } finally {
      setSaving(false);
    }
  };

  const handleAddStep = async () => {
    if (!newStep.name.trim()) return;

    try {
      setSaving(true);
      await devFilesApi.addDevPlanStep(newStep);
      setNewStep({ name: '', description: '', comment: '', status: 'Not Started', type: 'Core', owner: currentUser });
      setShowAddStep(false);
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to add step');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteStep = () => {
    if (!selectedStep) return;
    notify.confirm(`Delete Step ${selectedStep.step}: ${selectedStep.name}?`, async () => {
      try {
        setSaving(true);
        await devFilesApi.deleteDevPlanStep(selectedStep.step);
        setIsEditing(false);
        setSelectedStep(null);
        await loadDevPlan();
      } catch (err) {
        notify.error(err.response?.data?.message || 'Failed to delete step');
      } finally {
        setSaving(false);
      }
    });
  };


  const openCloseStepModal = () => {
    if (!selectedStep) return;
    setCloseStepForm({
      summary: `Step ${selectedStep.step}: ${selectedStep.name}`,
      changes: selectedStep.description || ''
    });
    setShowCloseStep(true);
  };

  const handleCloseStep = async () => {
    if (!selectedStep) return;
    try {
      setSaving(true);
      const changes = closeStepForm.changes
        .split('\n')
        .map(c => c.trim())
        .filter(c => c.length > 0);
      await devFilesApi.closeDevPlanStep(selectedStep.step, closeStepForm.summary, changes);
      setShowCloseStep(false);
      setCloseStepForm({ summary: '', changes: '' });
      // Force full page reload to pick up new Footer version
      window.location.reload();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to close step');
      setSaving(false);
    }
  };

  // Build completion renumber preview
  const openCompletePreview = () => {
    if (!selectedStep || !devPlan?.steps) return;
    const steps = devPlan.steps.map(s => ({ ...s }));
    const completingStep = steps.find(s => s.step === selectedStep.step);
    if (!completingStep) return;

    // Find where it should go: max completed step + 1
    const maxComplete = Math.max(0, ...steps.filter(s => s.status === 'Complete').map(s => s.step));
    const newPosition = maxComplete + 1;

    // Mark it complete
    completingStep.status = 'Complete';

    // Build new numbering: completed steps keep their numbers,
    // the completing step goes to newPosition, everything else shifts
    const completed = steps.filter(s => s.status === 'Complete' && s.step !== selectedStep.step)
      .sort((a, b) => a.step - b.step);
    const active = steps.filter(s => s.status !== 'Complete' && s.step !== selectedStep.step)
      .sort((a, b) => a.step - b.step);

    const result = [];
    // Completed steps keep their numbers
    completed.forEach(s => result.push({ ...s, newStep: s.step }));
    // The completing step goes to newPosition
    result.push({ ...completingStep, newStep: newPosition });
    // Active steps fill in after, starting at newPosition + 1
    let nextStep = newPosition + 1;
    active.forEach(s => {
      result.push({ ...s, newStep: nextStep });
      nextStep++;
    });

    // Sort by newStep for display
    result.sort((a, b) => a.newStep - b.newStep);
    setCompletePreviewItems(result);
    setShowCompletePreview(true);
  };

  // Apply completion renumbering
  const handleApplyComplete = async () => {
    if (!selectedStep) return;
    try {
      setSaving(true);
      // First close the step via the existing endpoint (marks complete + rev log)
      const changes = closeStepForm.changes?.split('\n').map(c => c.trim()).filter(c => c.length > 0) || [];
      await devFilesApi.closeDevPlanStep(selectedStep.step, closeStepForm.summary || `Step ${selectedStep.step}: ${selectedStep.name}`, changes);

      // Then bulk reorder all steps to new positions
      const updates = completePreviewItems.map(item => ({
        id: item.id,
        step: item.newStep,
        owner: item.owner
      }));
      await devFilesApi.reorderDevPlan(updates);

      setShowCompletePreview(false);
      setShowCloseStep(false);
      setCloseStepForm({ summary: '', changes: '' });
      window.location.reload();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to complete step');
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Complete':
        return <Check size={14} className="status-icon complete" />;
      case 'In Progress':
        return <Clock size={14} className="status-icon in-progress" />;
      default:
        return <Circle size={14} className="status-icon incomplete" />;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'Complete':
        return 'status-complete';
      case 'In Progress':
        return 'status-in-progress';
      default:
        return 'status-incomplete';
    }
  };

  // Calculate progress
  const getProgress = () => {
    if (!devPlan?.steps) return { complete: 0, total: 0, percent: 0 };
    const complete = devPlan.steps.filter(s => s.status === 'Complete').length;
    const total = devPlan.steps.length;
    return {
      complete,
      total,
      percent: Math.round((complete / total) * 100)
    };
  };

  // Filter steps by type, status, and owner
  const getFilteredSteps = () => {
    if (!devPlan?.steps) return [];
    let filtered = devPlan.steps;
    // Status filter
    if (statusFilter === 'Active') {
      filtered = filtered.filter(s => s.status !== 'Complete');
    } else if (statusFilter !== 'All') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }
    // Type filter
    if (typeFilter !== 'All') {
      filtered = filtered.filter(s => s.type === typeFilter);
    }
    // Owner filter
    if (ownerFilter !== 'all') {
      if (ownerFilter === 'unassigned') {
        filtered = filtered.filter(s => !s.owner);
      } else {
        filtered = filtered.filter(s => s.owner?.toLowerCase() === ownerFilter.toLowerCase());
      }
    }
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s =>
        s.name?.toLowerCase().includes(term) ||
        String(s.step).includes(term) ||
        s.owner?.toLowerCase().includes(term) ||
        s.description?.toLowerCase().includes(term)
      );
    }
    return filtered;
  };

  // Status counts for filter badges
  const getStatusCounts = () => {
    if (!devPlan?.steps) return {};
    const counts = {
      All: devPlan.steps.length,
      Active: devPlan.steps.filter(s => s.status !== 'Complete').length,
      'Not Started': devPlan.steps.filter(s => s.status === 'Not Started').length,
      'In Progress': devPlan.steps.filter(s => s.status === 'In Progress').length,
      Complete: devPlan.steps.filter(s => s.status === 'Complete').length
    };
    return counts;
  };

  // Open reorder modal — load all steps with current + new step numbers
  const openReorderModal = () => {
    if (!devPlan?.steps) return;
    const items = devPlan.steps
      .map(s => ({ id: s.id, step: s.step, newStep: s.step, name: s.name, owner: s.owner, type: s.type, status: s.status }))
      .sort((a, b) => a.newStep - b.newStep);
    setReorderItems(items);
    setShowReorderModal(true);
  };

  // Change a step's new number (no auto-sort)
  const handleReorderStepChange = (id, newStepValue) => {
    setReorderItems(prev => prev.map(item => item.id === id ? { ...item, newStep: newStepValue } : item));
  };

  // Re-sort: order by newStep, then auto-increment collisions
  const resortReorderItems = () => {
    setReorderItems(prev => {
      // Sort by newStep, tiebreak by original step
      const sorted = [...prev].sort((a, b) => a.newStep - b.newStep || a.step - b.step);
      // Walk through and bump collisions
      for (let i = 1; i < sorted.length; i++) {
        if (sorted[i].newStep <= sorted[i - 1].newStep) {
          sorted[i] = { ...sorted[i], newStep: sorted[i - 1].newStep + 1 };
        }
      }
      return sorted;
    });
  };

  // Check if reorder has changes
  const reorderHasChanges = reorderItems.some(item => item.step !== item.newStep || item.owner !== devPlan?.steps?.find(s => s.id === item.id)?.owner);

  // Save reorder
  const handleSaveReorder = async () => {
    const changedItems = reorderItems.filter(item => item.step !== item.newStep || item.owner !== devPlan?.steps?.find(s => s.id === item.id)?.owner);
    if (changedItems.length === 0) { notify.warning('No changes to save'); return; }
    try {
      setSaving(true);
      const updates = reorderItems.map(item => ({
        id: item.id,
        step: item.newStep,
        owner: item.owner
      }));
      await devFilesApi.reorderDevPlan(updates);
      notify.success(`Reordered ${changedItems.length} step(s)`);
      setShowReorderModal(false);
      await loadDevPlan();
    } catch (err) {
      notify.error(err.response?.data?.message || 'Failed to reorder');
    } finally {
      setSaving(false);
    }
  };

  // Get owner stats for filter (excludes Complete)
  const getOwnerStats = useCallback(() => {
    if (!devPlan?.steps) return {};
    const stats = {};
    devPlan.steps.forEach(step => {
      if (step.status === 'Complete') return; // Skip completed
      const owner = step.owner || 'unassigned';
      if (!stats[owner]) stats[owner] = { notStarted: 0, inProgress: 0, total: 0 };
      stats[owner].total++;
      if (step.status === 'Not Started') stats[owner].notStarted++;
      else if (step.status === 'In Progress') stats[owner].inProgress++;
    });
    return stats;
  }, [devPlan]);


  // Track if edit form has changes
  const hasChanges = isEditing && editForm && selectedStep && (
    editForm.name !== selectedStep.name ||
    editForm.description !== selectedStep.description ||
    editForm.comment !== selectedStep.comment ||
    editForm.status !== selectedStep.status ||
    editForm.type !== selectedStep.type ||
    editForm.owner !== selectedStep.owner
  );

  // Format owner name for display
  const formatOwner = (owner) => {
    if (!owner) return 'Unassigned';
    return owner.charAt(0).toUpperCase() + owner.slice(1);
  };

  // Get type badge class
  const getTypeClass = (type) => {
    switch (type) {
      case 'Core': return 'type-core';
      case 'Feature': return 'type-feature';
      case 'Milestone': return 'type-milestone';
      case 'Infrastructure': return 'type-infrastructure';
      default: return 'type-core';
    }
  };

  // Get type counts for filter badges
  const getTypeCounts = () => {
    if (!devPlan?.steps) return {};
    const counts = { All: devPlan.steps.length };
    STEP_TYPES.forEach(type => {
      counts[type] = devPlan.steps.filter(s => s.type === type).length;
    });
    return counts;
  };

  if (loading) {
    return <div className="dev-page loading">Loading...</div>;
  }

  const progress = getProgress();
  const filteredSteps = getFilteredSteps();
  const isFiltered = typeFilter !== 'All' || statusFilter !== 'Active' || ownerFilter !== 'all' || searchTerm;

  return (
    <div className="module-container">
      {/* Header - Pattern 1 */}
      <div className="module-header">
        <div className="module-header-left">
          <ClipboardList size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Dev Plan</h1>
          <span className="module-header-meta">
            {isFiltered ? `${filteredSteps.length} of ${devPlan?.steps?.length || 0}` : `${devPlan?.steps?.length || 0}`} steps &#8226; {progress.complete}/{progress.total} complete ({progress.percent}%)
          </span>
        </div>
        <div className="module-header-actions">
          {isEditing ? (
            <button className="crud-btn" onClick={handleDeleteStep} disabled={saving} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn" onClick={() => { setNewStep(prev => ({ ...prev, owner: currentUser })); setShowAddStep(true); }} title="Add Step"><Plus size={18} /></button>
          )}
          <button className={`crud-btn ${!selectedStep && !isEditing ? 'disabled' : ''}`} onClick={isEditing ? cancelEditing : startEditing} disabled={!selectedStep && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`crud-btn ${!isEditing || !hasChanges ? 'disabled' : ''}`} onClick={handleSave} disabled={!isEditing || saving || !hasChanges} title="Save"><Save size={18} /></button>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown" style={{ minWidth: '200px' }}>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); loadDevPlan(); }}><RefreshCw size={16} /><span>Refresh</span></button>
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); openReorderModal(); }}><GripVertical size={16} /><span>Re-sort & Assign</span></button>
                {selectedStep && selectedStep.status !== 'Complete' && (
                  <button className="action-menu-item" onClick={() => { setMenuOpen(false); openCloseStepModal(); }}><Check size={16} /><span>Complete Step</span></button>
                )}
                <div style={{ borderTop: '1px solid var(--color-gray-200)', margin: 'var(--spacing-xs) 0' }} />
                <div style={{ padding: 'var(--spacing-xs) var(--spacing-md)', fontSize: '10px', fontWeight: 600, color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Owner</div>
                {['all', ...OWNERS, 'Unassigned'].map(o => (
                  <button key={o} className={`action-menu-item ${ownerFilter === o ? 'selected' : ''}`} onClick={() => { setOwnerFilter(o); setMenuOpen(false); }} style={ownerFilter === o ? { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' } : undefined}>
                    <span>{o === 'all' ? 'All Owners' : o}</span>
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--color-gray-200)', margin: 'var(--spacing-xs) 0' }} />
                <div style={{ padding: 'var(--spacing-xs) var(--spacing-md)', fontSize: '10px', fontWeight: 600, color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Type</div>
                {['All', ...STEP_TYPES].map(t => (
                  <button key={t} className={`action-menu-item ${typeFilter === t ? 'selected' : ''}`} onClick={() => { setTypeFilter(t); setMenuOpen(false); }} style={typeFilter === t ? { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' } : undefined}>
                    <span>{t === 'All' ? 'All Types' : t}</span>
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--color-gray-200)', margin: 'var(--spacing-xs) 0' }} />
                <div style={{ padding: 'var(--spacing-xs) var(--spacing-md)', fontSize: '10px', fontWeight: 600, color: 'var(--color-gray-400)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Status</div>
                {['Active', 'All', 'Not Started', 'In Progress', 'Complete'].map(s => (
                  <button key={s} className={`action-menu-item ${statusFilter === s ? 'selected' : ''}`} onClick={() => { setStatusFilter(s); setMenuOpen(false); }} style={statusFilter === s ? { backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' } : undefined}>
                    <span>{s}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="dev-plan-progress">
        <div className="dev-plan-progress-bar" style={{ width: `${progress.percent}%` }} />
      </div>

      {/* Main content */}
      <div className="list-view">
        {listCollapsed ? (
          <div className="list-panel-collapsed">
            <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search steps..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>
            <div className="list-scroll">
              {filteredSteps.map((step) => (
                <button key={step.id} onClick={() => handleSelectStep(step)} className={`list-item-btn ${selectedStep?.step === step.step ? 'selected' : ''}`}>
                  <span className="list-item-primary">
                    <span style={{ color: 'var(--color-gray-400)', marginRight: '6px', fontSize: 'var(--text-xs)' }}>{step.step}</span>
                    {step.name}
                  </span>
                  <span className="list-item-secondary">
                    {step.type} &#8226; {step.owner || 'Unassigned'} &#8226; {step.status}
                  </span>
                </button>
              ))}
              {filteredSteps.length === 0 && <div className="task-list-empty">No steps match filters</div>}
            </div>
          </div>
        )}

        {/* Detail Panel */}
        <div className="detail-panel">
          {selectedStep ? (
            <>
              <div className="dev-detail-header">
                <div className="dev-detail-title">
                  <h2>
                    <span>Step {selectedStep.step}:</span>
                    {' '}{isEditing ? editForm.name : selectedStep.name}
                  </h2>
                  <span className={`step-type-badge ${getTypeClass(selectedStep.type)}`}>
                    {selectedStep.type || 'Core'}
                  </span>
                  <span className={`step-status-badge ${getStatusClass(selectedStep.status)}`}>
                    {selectedStep.status}
                  </span>
                  <span className="step-owner-badge">
                    {formatOwner(selectedStep.owner)}
                  </span>
                </div>
                <div className="dev-detail-actions">
                  <button
                    className={`dev-circle-btn ${
                      selectedStep?.status === 'Complete' ? 'success' :
                      selectedStep?.status === 'In Progress' ? 'warning' : ''
                    }`}
                    onClick={selectedStep?.status !== 'Complete' ? openCloseStepModal : undefined}
                    disabled={selectedStep?.status === 'Complete'}
                    title={
                      selectedStep?.status === 'Complete' ? 'Step Complete' :
                      selectedStep?.status === 'In Progress' ? 'Close Step (Major Version)' :
                      'Close Step (Major Version)'
                    }
                    style={selectedStep?.status === 'Complete' ? { cursor: 'default', opacity: 1 } : undefined}
                  >
                    <Flag size={16} />
                  </button>
                </div>
              </div>

              <div className="dev-detail-body">
                {isEditing ? (
                  <div className="dev-detail-form">
                    <div className="form-row">
                      <label>Name</label>
                      <input
                        type="text"
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        className="form-input"
                      />
                    </div>
                    <div className="form-row form-row-inline">
                      <div className="form-field">
                        <label>Type</label>
                        <select
                          value={editForm.type || 'Core'}
                          onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                          className="form-input"
                        >
                          {STEP_TYPES.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Status</label>
                        <select
                          value={editForm.status}
                          onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                          className="form-input"
                        >
                          <option value="Not Started">Not Started</option>
                          <option value="In Progress">In Progress</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Owner</label>
                        <select
                          value={editForm.owner || ''}
                          onChange={(e) => setEditForm({ ...editForm, owner: e.target.value || null })}
                          className="form-input"
                        >
                          <option value="">Unassigned</option>
                          {OWNERS.map(owner => (
                            <option key={owner} value={owner}>{formatOwner(owner)}</option>
                          ))}
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Step</label>
                        <input className="form-input derived-field" value={selectedStep.step} readOnly />
                      </div>
                    </div>
                    <div className="form-row">
                      <label>Description</label>
                      <textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                        className="form-input"
                        rows={9}
                      />
                    </div>
                    <div className="form-row">
                      <label>Notes</label>
                      <textarea
                        value={editForm.comment}
                        onChange={(e) => setEditForm({ ...editForm, comment: e.target.value })}
                        className="form-input"
                        rows={15}
                        placeholder="Additional details, sub-tasks, or implementation notes..."
                      />
                    </div>
                  </div>
                ) : (
                  <div className="dev-detail-view">
                    <div className="detail-section">
                      <label>Description</label>
                      <p>{selectedStep.description || <em className="text-muted">No description</em>}</p>
                    </div>
                    {selectedStep.comment && (
                      <div className="detail-section">
                        <label>Notes</label>
                        <pre className="detail-notes">{selectedStep.comment}</pre>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="dev-detail-empty">
              <p>Select a step to view details</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Step Modal */}
      {showAddStep && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Add Step</h3>
              <button onClick={() => setShowAddStep(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <label>Name</label>
                <input
                  type="text"
                  className="form-input"
                  value={newStep.name}
                  onChange={(e) => setNewStep({ ...newStep, name: e.target.value })}
                  placeholder="Step name"
                  autoFocus
                />
              </div>
              <div className="form-row">
                <label>Description</label>
                <textarea
                  className="form-input"
                  value={newStep.description}
                  onChange={(e) => setNewStep({ ...newStep, description: e.target.value })}
                  placeholder="What does this step involve?"
                  rows={9}
                />
              </div>
              <div className="form-row">
                <label>Notes (optional)</label>
                <textarea
                  className="form-input"
                  value={newStep.comment}
                  onChange={(e) => setNewStep({ ...newStep, comment: e.target.value })}
                  placeholder="Additional details or sub-tasks"
                  rows={12}
                />
              </div>
              <div className="form-row form-row-inline">
                <div className="form-field">
                  <label>Type</label>
                  <select
                    className="form-input"
                    value={newStep.type}
                    onChange={(e) => setNewStep({ ...newStep, type: e.target.value })}
                  >
                    {STEP_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label>Status</label>
                  <select
                    className="form-input"
                    value={newStep.status}
                    onChange={(e) => setNewStep({ ...newStep, status: e.target.value })}
                  >
                    <option value="Not Started">Not Started</option>
                    <option value="In Progress">In Progress</option>
                    <option value="Complete">Complete</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Owner</label>
                  <select
                    className="form-input"
                    value={newStep.owner || ''}
                    onChange={(e) => setNewStep({ ...newStep, owner: e.target.value || null })}
                  >
                    <option value="">Unassigned</option>
                    {OWNERS.map(owner => (
                      <option key={owner} value={owner}>{formatOwner(owner)}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddStep(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddStep}
                disabled={saving || !newStep.name.trim()}
              >
                {saving ? 'Adding...' : 'Add Step'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Step Modal */}
      {showCloseStep && selectedStep && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Close Step {selectedStep.step}</h3>
              <button onClick={() => setShowCloseStep(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="close-step-info">
                <p><strong>This will:</strong></p>
                <ul>
                  <li>Mark Step {selectedStep.step} as Complete</li>
                  <li>Increment major version (v1.x → v2.000)</li>
                  <li>Add entry to Rev Log</li>
                  <li>Update Footer version</li>
                </ul>
              </div>
              <div className="form-row">
                <label>Release Summary</label>
                <input
                  type="text"
                  className="form-input"
                  value={closeStepForm.summary}
                  onChange={(e) => setCloseStepForm({ ...closeStepForm, summary: e.target.value })}
                  placeholder="Summary for rev log"
                  autoFocus
                />
              </div>
              <div className="form-row">
                <label>Changes (one per line)</label>
                <textarea
                  className="form-input"
                  value={closeStepForm.changes}
                  onChange={(e) => setCloseStepForm({ ...closeStepForm, changes: e.target.value })}
                  placeholder="Key changes/features delivered..."
                  rows={18}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowCloseStep(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => { openCompletePreview(); }}
                disabled={!closeStepForm.summary.trim()}
              >
                Preview & Complete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Reorder Modal */}
      {showReorderModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>Reorder & Assign Steps</h3>
              <button onClick={() => setShowReorderModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto', padding: 0 }}>
              <table className="data-grid" style={{ fontSize: 'var(--text-xs)' }}>
                <thead>
                  <tr>
                    <th style={{ width: '60px' }}>Current</th>
                    <th style={{ width: '70px' }}>New Step</th>
                    <th style={{ width: '30px' }}></th>
                    <th>Name</th>
                    <th style={{ width: '100px' }}>Owner</th>
                    <th style={{ width: '90px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reorderItems.map(item => {
                    const changed = item.step !== item.newStep;
                    const ownerChanged = item.owner !== devPlan?.steps?.find(s => s.id === item.id)?.owner;
                    return (
                      <tr key={item.id} style={changed ? { backgroundColor: '#fffde7' } : undefined}>
                        <td style={{ textAlign: 'center', color: 'var(--color-gray-500)' }}>{item.step}</td>
                        <td>
                          <input
                            type="number"
                            className={`form-input${changed ? ' dirty' : ''}`}
                            value={item.newStep}
                            onChange={e => handleReorderStepChange(item.id, parseInt(e.target.value) || 0)}
                            style={{ height: '26px', width: '60px', textAlign: 'center', padding: '2px 4px', fontSize: '12px' }}
                            min={1}
                          />
                        </td>
                        <td>
                          <span className={`reorder-type ${getTypeClass(item.type)}`} style={{ fontSize: '10px' }}>{item.type?.charAt(0)}</span>
                        </td>
                        <td style={{ fontWeight: 500 }}>{item.name}</td>
                        <td>
                          <select
                            className="form-input"
                            value={item.owner || ''}
                            onChange={e => setReorderItems(prev => prev.map(i => i.id === item.id ? { ...i, owner: e.target.value || null } : i))}
                            style={{ height: '26px', fontSize: '11px', padding: '2px 4px', backgroundColor: ownerChanged ? '#fffde7' : undefined }}
                          >
                            <option value="">Unassigned</option>
                            {OWNERS.map(o => <option key={o} value={o}>{o}</option>)}
                            <option value="All">All</option>
                          </select>
                        </td>
                        <td style={{ fontSize: '11px', color: 'var(--color-gray-500)' }}>{item.status}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="modal-footer">
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                {reorderHasChanges ? `${reorderItems.filter(i => i.step !== i.newStep).length} step(s) changed` : 'No changes'}
              </span>
              <button className="btn btn-secondary" onClick={resortReorderItems}>Re-sort</button>
              <button className="btn btn-secondary" onClick={() => setShowReorderModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSaveReorder} disabled={saving || !reorderHasChanges}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Complete Step Preview Modal */}
      {showCompletePreview && selectedStep && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '700px', maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>Complete Step {selectedStep.step}: {selectedStep.name}</h3>
              <button onClick={() => setShowCompletePreview(false)}><X size={20} /></button>
            </div>
            <div className="modal-body" style={{ padding: 0 }}>
              <div style={{ padding: 'var(--spacing-sm) var(--spacing-lg)', fontSize: 'var(--text-xs)', color: 'var(--color-gray-600)', borderBottom: '1px solid var(--color-gray-200)', backgroundColor: 'var(--color-gray-50)' }}>
                Step {selectedStep.step} will move to position {completePreviewItems.find(i => i.id === selectedStep.id)?.newStep || '?'} and remaining steps will renumber.
              </div>
              <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <table className="data-grid" style={{ fontSize: 'var(--text-xs)' }}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>Was</th>
                      <th style={{ width: '50px' }}>Now</th>
                      <th>Name</th>
                      <th style={{ width: '80px' }}>Owner</th>
                      <th style={{ width: '80px' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completePreviewItems.map(item => {
                      const changed = item.step !== item.newStep;
                      const isCompleting = item.id === selectedStep.id;
                      return (
                        <tr key={item.id} style={{
                          backgroundColor: isCompleting ? 'var(--color-success-light)' : changed ? '#fffde7' : undefined,
                          fontWeight: isCompleting ? 600 : undefined
                        }}>
                          <td style={{ textAlign: 'center', color: 'var(--color-gray-400)' }}>{item.step}</td>
                          <td style={{ textAlign: 'center', fontWeight: changed ? 600 : 400, color: changed ? 'var(--color-primary)' : undefined }}>{item.newStep}</td>
                          <td>{item.name}</td>
                          <td>{item.owner || 'Unassigned'}</td>
                          <td style={{ color: isCompleting ? 'var(--color-success)' : 'var(--color-gray-500)' }}>{isCompleting ? 'Complete' : item.status}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="modal-footer">
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-500)' }}>
                {completePreviewItems.filter(i => i.step !== i.newStep).length} step(s) will renumber
              </span>
              <button className="btn btn-secondary" onClick={() => setShowCompletePreview(false)}>Back</button>
              <button className="btn btn-success" onClick={handleApplyComplete} disabled={saving}>
                {saving ? 'Completing...' : 'Complete & Renumber'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DevPlanPage;
